import { deriveLessonInviteMetadata } from './lessonInvites';

export function createLessonInstancePayload(lessonPlan = {}, { status = 'draft' } = {}) {
  const normalizedEmail = String(lessonPlan.studentEmail || '').trim().toLowerCase();
  const inviteMetadata = deriveLessonInviteMetadata(
    {
      ...lessonPlan,
      studentEmailNormalized: normalizedEmail,
    },
    { lessonId: lessonPlan.lessonId || '' }
  );
  return {
    videoId: lessonPlan.videoId || '',
    organizationId: lessonPlan.organizationId || '',
    studentId: lessonPlan.studentId || '',
    studentName: lessonPlan.studentName || '',
    studentEmail: lessonPlan.studentEmail || '',
    studentEmailNormalized: normalizedEmail,
    inviteStatus: inviteMetadata.inviteStatus,
    inviteSentAt: inviteMetadata.inviteSentAt,
    inviteLinkPath: inviteMetadata.inviteLinkPath,
    inviteLastError: inviteMetadata.inviteLastError,
    instructorId: lessonPlan.instructorId || '',
    // Pass 66 — the instructor's Auth uid (SEPARATE from the canonical business instructorId). Firestore
    // Rules match the assigned instructor on THIS field, since instructorId is a member id, not an Auth uid.
    instructorAuthUserId: lessonPlan.instructorAuthUserId || '',
    assignedInstructorId: lessonPlan.assignedInstructorId || lessonPlan.instructorId || '',
    assignmentSource: lessonPlan.assignmentSource || '',
    sport: lessonPlan.sport || '',
    trackId: lessonPlan.trackId || '',
    targetSkillId: lessonPlan.targetSkillId || '',
    lessonFlowId: lessonPlan.lessonFlowId || '',
    recommendedBySystem: Boolean(lessonPlan.recommendedBySystem),
    recommendationReason: lessonPlan.recommendationReason || '',
    stages: (lessonPlan.stages || []).map((stage) => ({
      stage: stage.stage,
      plannedDrillIds: stage.plannedDrillIds || [],
      instructorNotes: stage.instructorNotes || '',
      completionStatus: stage.completionStatus || 'not_attempted',
    })),
    lessonOutcome: {
      status: lessonPlan.lessonOutcome?.status || 'in_progress',
      skillConfidence: lessonPlan.lessonOutcome?.skillConfidence || 'developing',
      recommendedNextSkillId: lessonPlan.lessonOutcome?.recommendedNextSkillId || '',
      instructorSummary: lessonPlan.lessonOutcome?.instructorSummary || '',
    },
    // Pass 66 — durable booking → lessonInstance LINEAGE (additive; blank for standalone/manual lessons).
    // clientId is the commercial/customer anchor (Pass 63); it is NOT the participant's Auth identity —
    // studentId/clientUserId stay whatever the plan explicitly carried (never fabricated from clientId).
    origin: lessonPlan.origin || '',                 // 'booking' | 'manual' | ''
    bookingId: lessonPlan.bookingId || '',
    bookingRequestId: lessonPlan.bookingRequestId || '',
    requestLineId: lessonPlan.requestLineId || '',
    requestSlotId: lessonPlan.requestSlotId || '',
    clientId: lessonPlan.clientId || '',
    scheduledAt: lessonPlan.scheduledAt || null,
    serviceDate: lessonPlan.serviceDate || '',
    location: lessonPlan.location || '',
    groupSize: Number.isFinite(+lessonPlan.groupSize) ? Math.floor(+lessonPlan.groupSize) : null,
    status,
  };
}

export function normalizeLessonInstanceRecord(docSnapOrData, fallbackId = '') {
  const hasSnapshotShape = typeof docSnapOrData?.data === 'function';
  const rawData = hasSnapshotShape ? docSnapOrData.data() || {} : (docSnapOrData || {});
  const lessonId = hasSnapshotShape ? docSnapOrData.id : rawData.lessonId || fallbackId;

  return {
    lessonId: lessonId || fallbackId || '',
    videoId: rawData.videoId || '',
    studentId: rawData.studentId || '',
    studentName: rawData.studentName || '',
    studentEmail: rawData.studentEmail || '',
    studentEmailNormalized:
      rawData.studentEmailNormalized || String(rawData.studentEmail || '').trim().toLowerCase(),
    inviteStatus: rawData.inviteStatus || (rawData.studentId ? 'not_needed' : rawData.studentEmail ? 'pending' : 'not_needed'),
    inviteSentAt: rawData.inviteSentAt || null,
    inviteLinkPath: rawData.inviteLinkPath || '',
    inviteLastError: rawData.inviteLastError || '',
    instructorId: rawData.instructorId || '',
    instructorAuthUserId: rawData.instructorAuthUserId || '',
    organizationId: rawData.organizationId || '',
    assignedInstructorId: rawData.assignedInstructorId || rawData.instructorId || '',
    assignmentSource: rawData.assignmentSource || '',
    sport: rawData.sport || '',
    trackId: rawData.trackId || '',
    targetSkillId: rawData.targetSkillId || '',
    lessonFlowId: rawData.lessonFlowId || '',
    recommendedBySystem: Boolean(rawData.recommendedBySystem),
    recommendationReason: rawData.recommendationReason || '',
    stages: Array.isArray(rawData.stages)
      ? rawData.stages.map((stage) => ({
          stage: stage?.stage || '',
          plannedDrillIds: Array.isArray(stage?.plannedDrillIds) ? stage.plannedDrillIds : [],
          instructorNotes: stage?.instructorNotes || '',
          completionStatus: stage?.completionStatus || 'not_attempted',
        }))
      : [],
    lessonOutcome: {
      status: rawData.lessonOutcome?.status || 'in_progress',
      skillConfidence: rawData.lessonOutcome?.skillConfidence || 'developing',
      recommendedNextSkillId: rawData.lessonOutcome?.recommendedNextSkillId || '',
      instructorSummary: rawData.lessonOutcome?.instructorSummary || '',
    },
    // Pass 66 — booking lineage preserved through the read boundary (blank for standalone lessons).
    origin: rawData.origin || '',
    bookingId: rawData.bookingId || '',
    bookingRequestId: rawData.bookingRequestId || '',
    requestLineId: rawData.requestLineId || '',
    requestSlotId: rawData.requestSlotId || '',
    clientId: rawData.clientId || '',
    scheduledAt: rawData.scheduledAt || null,
    serviceDate: rawData.serviceDate || '',
    location: rawData.location || '',
    groupSize: Number.isFinite(+rawData.groupSize) ? Math.floor(+rawData.groupSize) : null,
    status: rawData.status || 'draft',
    createdAt: rawData.createdAt || null,
    updatedAt: rawData.updatedAt || null,
  };
}
